import { useQuery } from '@apollo/client';
import * as React from 'react';

import { AIEmailHandlingData, LLMOutreachData, PERSON_AI_COMMUNICATION_DATA } from '../graphql/queries/communications';
import { Communication } from '../state';
import { useReduxState } from './use-redux';

interface CommunicationsAIData {
    getCommunicationAIActions?: (communication: Communication) => AIEmailHandlingData[];
    getCommunicationOutreachGenInfo?: (communication: Communication) => LLMOutreachData[];
}

const CommunicationsAIDataContext = React.createContext<CommunicationsAIData | null>(null);

export const useCommunicationsAIData = (): CommunicationsAIData => {
    const context = React.useContext(CommunicationsAIDataContext);
    if (!context) {
        return {};
    }
    return context;
};

interface CommunicationsAIDataProviderProps {
    personId: string;
    jobId?: string;
    children: React.ReactNode;
}

export const CommunicationsAIDataProvider: React.FC<CommunicationsAIDataProviderProps> = ({
    personId,
    jobId,
    children
}) => {
    const { data: aiData } = useQuery<
        { aiEmailActions: AIEmailHandlingData[]; llmOutreaches: LLMOutreachData[] },
        { personId: string; jobId?: string }
    >(PERSON_AI_COMMUNICATION_DATA, { variables: { personId, jobId } });

    const communicationsFromState = useReduxState((state) => state.communications.get(personId));
    const communications = React.useMemo(() => communicationsFromState || [], [communicationsFromState]);

    const aiEmailActions = aiData?.aiEmailActions || [];
    const llmOutreaches = aiData?.llmOutreaches || [];

    // Build lookup map: Gmail messageId → RFC Message-ID
    const messageIdToRfcId = React.useMemo(() => {
        const map = new Map<string, string>();
        communications.forEach((comm) => {
            if (comm.referenceMessageId) {
                map.set(comm.messageId, comm.referenceMessageId);
            }
        });
        return map;
    }, [communications]);

    const getCommunicationAIActions = React.useCallback(
        (communication: Communication): AIEmailHandlingData[] => {
            return aiEmailActions.filter((action) => {
                if (action.replyToAccount !== communication.account) {
                    return false;
                }

                // Case 1: This communication IS the original message AI replied to
                if (action.replyToMessageId === communication.messageId) {
                    return true;
                }

                // Case 2: This communication IS the AI-generated reply
                // Lookup RFC Message-ID for the Gmail messageId stored in action
                const originalRfcId = messageIdToRfcId.get(action.replyToMessageId);
                if (originalRfcId && originalRfcId === communication.referenceInReplyTo) {
                    return true;
                }

                return false;
            });
        },
        [aiEmailActions, messageIdToRfcId]
    );

    const getCommunicationOutreachGenInfo = React.useCallback(
        (communication: Communication): LLMOutreachData[] => {
            // Check if this is part of an outreach sequence
            const isPartOfSequence = () => {
                // Not a reply - definitely an outreach
                if (communication.referenceInReplyTo === null) {
                    return true;
                }

                // Get all previous messages in this thread (same threadId, sent before this one)
                const previousMessages = communications.filter(
                    (comm) =>
                        comm.threadId === communication.threadId &&
                        comm.account === communication.account &&
                        comm.internalDate < communication.internalDate
                );

                // All previous messages must have the same sender as current message
                return (
                    communication.account === communication.sender &&
                    previousMessages.every((comm) => comm.sender === communication.sender)
                );
            };

            if (!isPartOfSequence()) {
                return [];
            }

            return llmOutreaches.filter((outreach) => {
                // Check if this communication matches any send for this outreach
                const matchesSend = outreach.recipient.sends.some(
                    (send) =>
                        send.communication.messageId === communication.messageId &&
                        send.communication.account === communication.account
                );

                return matchesSend && (!jobId || outreach.jobId === jobId);
            });
        },
        [llmOutreaches, communications, jobId]
    );

    const communicationAIDataContext = React.useMemo(
        () => ({
            getCommunicationAIActions,
            getCommunicationOutreachGenInfo
        }),
        [getCommunicationAIActions, getCommunicationOutreachGenInfo]
    );

    return (
        <CommunicationsAIDataContext.Provider value={communicationAIDataContext}>
            {children}
        </CommunicationsAIDataContext.Provider>
    );
};
